import { Helmet } from 'react-helmet-async';

/**
 * SEOHead - Dynamic meta tags for social sharing and SEO
 * 
 * Usage:
 * <SEOHead 
 *   title="Page Title"
 *   description="Page description"
 *   image="https://example.com/image.jpg"
 *   url="https://example.com/page"
 * />
 */
const SEOHead = ({ 
  title = "StoryWeaver - AI Storybook Creator for Kids",
  description = "Create personalized AI-generated storybooks starring your children. Transform photos into illustrated characters with multiple art styles. Print real books or read digitally.",
  image = "https://static.prod-images.emergentagent.com/jobs/b27ff5d2-bfc6-4eb1-8ae0-645df4fbd424/images/97d2717720f557097c6c55e6ec11a3b0835a70e9b8fc00f6f6ce72df2059a95b.png",
  url,
  type = "website",
  noIndex = false,
  structuredData = null,
  children
}) => {
  const siteUrl = "https://storyweavestudio.com";
  const fullUrl = url ? `${siteUrl}${url}` : siteUrl;
  const fullTitle = title.includes("StoryWeaver") ? title : `${title} | StoryWeaver`;
  
  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      {noIndex && <meta name="robots" content="noindex, nofollow" />}
      <link rel="canonical" href={fullUrl} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="StoryWeaver" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      
      {/* Structured Data (JSON-LD) */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
      
      {children}
    </Helmet>
  );
};

// Pre-built structured data generators
export const generateWebsiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "StoryWeaver",
  "alternateName": "StoryWeaver Studio",
  "url": "https://storyweavestudio.com",
  "description": "AI-powered personalized storybook creator for children",
  "potentialAction": {
    "@type": "SearchAction",
    "target": "https://storyweavestudio.com/search?q={search_term_string}",
    "query-input": "required name=search_term_string"
  }
});

export const generateOrganizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "StoryWeaver",
  "url": "https://storyweavestudio.com",
  "logo": "https://storyweavestudio.com/logo.png",
  "description": "Create magical AI-generated storybooks where your child is the hero",
  "sameAs": [
    // Add social media URLs when available
  ],
  "contactPoint": {
    "@type": "ContactPoint",
    "contactType": "customer service",
    "email": "support@storyweavestudio.com"
  }
});

export const generateProductSchema = (plan) => ({
  "@context": "https://schema.org",
  "@type": "Product",
  "name": `StoryWeaver ${plan.name}`,
  "description": plan.description,
  "brand": {
    "@type": "Brand",
    "name": "StoryWeaver"
  },
  "offers": {
    "@type": "Offer",
    "price": plan.price,
    "priceCurrency": "USD",
    "availability": "https://schema.org/InStock",
    "url": "https://storyweavestudio.com/pricing"
  }
});

export const generateSoftwareAppSchema = () => ({
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "name": "StoryWeaver",
  "applicationCategory": "EducationalApplication",
  "operatingSystem": "Web Browser",
  "offers": {
    "@type": "Offer",
    "price": "0",
    "priceCurrency": "USD"
  },
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "4.8",
    "ratingCount": "150",
    "bestRating": "5",
    "worstRating": "1"
  },
  "description": "Create personalized AI-generated storybooks starring your children",
  "screenshot": "https://storyweavestudio.com/screenshot.png"
});

export const generateFAQSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
});

export const generateBreadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": items.map((item, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "name": item.name,
    "item": item.url
  }))
});

export const generateHowToSchema = (steps) => ({
  "@context": "https://schema.org",
  "@type": "HowTo",
  "name": "How to Create a Personalized Storybook",
  "description": "Create a custom AI-generated storybook with your child as the hero",
  "step": steps.map((step, index) => ({
    "@type": "HowToStep",
    "position": index + 1,
    "name": step.name,
    "text": step.text,
    "image": step.image
  }))
});

export default SEOHead;
